/**
 * Backend contract used by SharepointComponent so the same browser can run
 * against the legacy sharepoint module (wells, operations, projects) or the
 * project-scoped well-examination module.
 */

export type SharepointAction =
	| "upload"
	| "createFolder"
	| "rename"
	| "move"
	| "delete"
	| "collab"
	| "download"
	| "preview";

export const ALL_SHAREPOINT_ACTIONS: ReadonlySet<SharepointAction> =
	new Set<SharepointAction>([
		"upload",
		"createFolder",
		"rename",
		"move",
		"delete",
		"collab",
		"download",
		"preview",
	]);

/** What an external well examiner may do inside the Well examination folder. */
export const EXAMINER_SHAREPOINT_ACTIONS: ReadonlySet<SharepointAction> =
	new Set<SharepointAction>(["upload", "download", "preview"]);

/** Graph drive item as returned by the server (shape is Graph's, kept loose). */
export type SharepointItem = {
	id: string;
	name?: string;
	file?: { mimeType?: string };
	folder?: Record<string, unknown>;
	[key: string]: any;
};

/** Mirror row from the tenant's sharepoint collection. */
export type SharepointOwnerRow = {
	item_id: string;
	owner_email?: string;
	item_type?: "file" | "folder";
	folder_hierarchy?: string[];
	createdAt?: string;
	[key: string]: any;
};

export interface SharepointFolderRef {
	id: string;
	name: string;
}

/** Where an item sits below a documents root ("Contents"). */
export interface SharepointFolderPath {
	/** Folders between the root (exclusive) and the item, top-down. */
	folders: SharepointFolderRef[];
	item: SharepointFolderRef;
}

export interface SharepointRoot {
	folderId: string | null;
	containerId: string | null;
}

export interface SharepointListing {
	items: SharepointItem[];
	folderId: string | null;
	containerId: string | null;
	/** Present when the backend returns owner rows with the listing. */
	owners?: SharepointOwnerRow[];
}

export interface SharepointSearchResponse {
	results: any[];
	total: number;
}

export interface SharepointApi {
	getRoot(): Promise<SharepointRoot>;
	listItems(
		folderId: string | null,
		containerId: string | null
	): Promise<SharepointListing>;
	getOwners(): Promise<SharepointOwnerRow[]>;
	createFolder(
		name: string,
		folderId: string,
		containerId: string,
		folderHierarchyIds: string[]
	): Promise<unknown>;
	uploadFiles(
		files: File[],
		folderId: string,
		containerId: string,
		folderHierarchyIds: string[],
		socketId?: string
	): Promise<any>;
	renameItem(
		itemId: string,
		containerId: string,
		newName: string
	): Promise<any>;
	deleteItems(
		itemIds: string[],
		containerId: string,
		socketId?: string
	): Promise<any>;
	moveItems(
		itemIds: string[],
		moveToId: string,
		containerId: string
	): Promise<any>;
	getFile(itemId: string, containerId: string): Promise<any>;
	getThumbnail(itemId: string, containerId: string): Promise<any>;
	getPreview(itemId: string, containerId: string): Promise<any>;
	/** Resolves the direct download URL for a listed item, or null. */
	getDownloadUrl(
		record: SharepointItem,
		containerId: string
	): Promise<string | null>;
	search(query: string, limit?: number): Promise<SharepointSearchResponse>;
	collaborate(itemId: string, containerId: string): Promise<any>;
	/** Client route that previews the item, relative to the site origin. */
	previewPath(itemId: string, containerId: string, currentPath: string): string;
}
